import { memo, ReactNode } from 'react';
import { Noto_Sans } from 'next/font/google';

export const monospaceFontTheme = Noto_Sans({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  display: 'swap',
  fallback: ['Roboto', 'Arial', 'sans-serif'],
  variable: '--monospace',
});

import Box, { BoxProps } from '@mui/material/Box';
import { ColumnDef } from '@tanstack/react-table';

import { TableStyled, TBodyStyled, TdStyled } from '../../styles';
import { useTableContext } from '../with-provider';
import { TableEmptyPlaceholder } from './placeholder';
import { TableLayoutHelper } from '../../core/table-layout-helper';
import { CellRender } from '../cell-render';
import { CellHeaderRender } from '../cell-header-render';

export const TableComponent = memo<ITableComponentProps>(
  ({
    tableRef,
    loading,
    containerProps,
    variant = 'table',
    emptyPlaceholder = 'Nenhuma simulação encontrada.',
    textColor,
    backgroundColor,
    noHeader = false,
  }) => {
    const { table, trophyColumns } = useTableContext();

    if (!table) return null;

    const isEmpty = !loading && table.getRowModel().rows.length === 0;

    return (
      <Box
        flex={1}
        bgcolor="#F6F6F6"
        display="flex"
        overflow="hidden"
        mt={1}
        width="100%"
        borderRadius={1}
        sx={{
          overflow: 'auto',
          border: '1px solid #E3E3E3',
          ...containerProps?.sx,
        }}
        {...containerProps}
      >
        <Box width="100%">
          <TableStyled ref={tableRef}>
            {!noHeader && (
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <CellHeaderRender
                        key={header.id}
                        header={header}
                        variant={variant}
                        backgroundColor={backgroundColor}
                        textColor={textColor}
                        table={table}
                      />
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <TBodyStyled>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const showTrophy = TableLayoutHelper.shouldShowTrophy(
                      cell.column.columnDef,
                      row,
                      trophyColumns
                    );

                    return (
                      <TdStyled
                        key={cell.id}
                        variant={variant}
                        component={
                          (cell.column.columnDef?.meta as any)?.isHead
                            ? 'th'
                            : 'td'
                        }
                        className={`${monospaceFontTheme.variable}`}
                        style={TableLayoutHelper.getCellStyles({ cell })}
                      >
                        <CellRender cell={cell} showTrophy={showTrophy} />
                      </TdStyled>
                    );
                  })}
                </tr>
              ))}
            </TBodyStyled>
          </TableStyled>

          <TableEmptyPlaceholder
            emptyPlaceholder={emptyPlaceholder}
            isEmpty={isEmpty}
          />
        </Box>
      </Box>
    );
  }
);

export interface ITableComponentProps {
  containerProps?: BoxProps;
  variant?: 'table' | 'list';
  tableRef?: any;
  emptyPlaceholder?: string | ReactNode;
  title?: string;
  data: any[];
  columns: ColumnDef<any>[];
  includeRowSelection?: boolean;
  pagination?: boolean;
  loading?: boolean;
  onApplyChanges?: () => void;
  backgroundColor?: string;
  textColor?: string;
  noHeader?: boolean;
  disableSort?: boolean;
  checkboxSize?: number;
}
